import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, CardBody, Button, Alert } from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import { FaUsers, FaUser, FaTrophy, FaMedal } from 'react-icons/fa';
import api from '../../services/api';
import LeagueScheduleSelector from './LeagueScheduleSelector';
import TeamPlayerForm from './TeamPlayerForm';
import LeagueRegistrationConfirm from './LeagueRegistrationConfirm';
import styles from '../../styles/public/league-registration.module.css';

const LeagueRegistrationForm = ({ league, organization }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [checkingTeamName, setCheckingTeamName] = useState(false);
    const [teamNameAvailable, setTeamNameAvailable] = useState(null);
    const [registrationData, setRegistrationData] = useState({
        league: league,
        schedule: null,
        otherScheduleSelected: false,
        scoringSchedule: null,
        preferredDay: '',
        preferredTimeslot: null,
        teamName: '',
        player1: {
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            tees: ''
        },
        player2: {
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            tees: ''
        },
        notes: ''
    });

    const fetchSchedules = useCallback(async () => {
        try {
            const response = await api.get(`/league-schedules/?league=${league.id}`);
            setSchedules(response.data);
        } catch (err) {
            console.error('Error fetching schedules:', err);
            setError('Failed to load available schedules');
        }
    }, [league.id]);

    useEffect(() => {
        if (league?.id) {
            fetchSchedules();
        }
    }, [league?.id, fetchSchedules]);

    useEffect(() => {
        const checkTeamName = async () => {
            const teamName = registrationData.teamName.trim();
            
            // Reset if empty or too short
            if (!teamName || teamName.length < 3) {
                setTeamNameAvailable(null);
                return;
            }

            setCheckingTeamName(true);
            try {
                const response = await api.get(
                    `/leagues/${league.guid}/check-team-name/?name=${encodeURIComponent(teamName)}`
                );
                setTeamNameAvailable(response.data.available);
            } catch (err) {
                console.error('Error checking team name:', err);
                setTeamNameAvailable(null);
            } finally {
                setCheckingTeamName(false);
            }
        };

        // Debounce the check
        const timer = setTimeout(checkTeamName, 500);
        return () => clearTimeout(timer);
    }, [registrationData.teamName, league.guid]);

    const updateRegistrationData = (data) => {
        setRegistrationData(prev => ({ ...prev, ...data }));
    };

    const validateStep = (step) => {
        setError(null);

        if (step === 1) {
            // Combined validation: Team info, player info, and schedule selection
            if (!registrationData.teamName.trim()) {
                setError('Team name is required');
                return false;
            }
            if (registrationData.teamName.trim().length < 3) {
                setError('Team name must be at least 3 characters');
                return false;
            }
            if (teamNameAvailable === false) {
                setError('This team name is already taken. Please choose a different name.');
                return false;
            }
            if (teamNameAvailable === null && registrationData.teamName.trim().length >= 3) {
                setError('Please wait while we check team name availability');
                return false;
            }

            const p1 = registrationData.player1;
            if (!p1.first_name.trim()) {
                setError('Player 1 first name is required');
                return false;
            }
            if (!p1.last_name.trim()) {
                setError('Player 1 last name is required');
                return false;
            }
            if (!p1.email.trim()) {
                setError('Player 1 email is required');
                return false;
            }
            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(p1.email)) {
                setError('Please enter a valid email address for Player 1');
                return false;
            }
            if (!p1.phone_number.trim()) {
                setError('Player 1 phone number is required');
                return false;
            }

            // Validate Player 2 email if provided
            const p2 = registrationData.player2;
            if (p2.email.trim() && !emailRegex.test(p2.email)) {
                setError('Please enter a valid email address for Player 2');
                return false;
            }

            if (!registrationData.schedule && !registrationData.otherScheduleSelected) {
                setError('Please select a schedule to continue');
                return false;
            }
            if (registrationData.otherScheduleSelected && !registrationData.preferredTimeslot) {
                setError('Please select a preferred day and time to continue');
                return false;
            }
            if (registrationData.otherScheduleSelected && !registrationData.scoringSchedule) {
                setError('Please select the schedule used for scoring to continue');
                return false;
            }

            return true;
        }

        return true;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => {
        setError(null);
        setCurrentStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        if (!validateStep(1)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Register team with all data in one request
            await api.post('/register-team/', {
                league: league.id,
                ...(registrationData.schedule && { schedule: registrationData.schedule.id }),
                ...(registrationData.otherScheduleSelected && {
                    schedule: registrationData.scoringSchedule.id,
                    day_of_week: registrationData.preferredDay,
                    start: registrationData.preferredTimeslot.start,
                    end: registrationData.preferredTimeslot.end
                }),
                teamName: registrationData.teamName,
                player1: registrationData.player1,
                player2: registrationData.player2,
                notes: registrationData.notes
            });

            setSuccess(true);
            window.scrollTo(0, 0);
        } catch (err) {
            console.error('Error submitting registration:', err);
            
            // Handle schedule full error (409 Conflict)
            if (err.response?.status === 409) {
                setError('The selected schedule is now full. Please go back and select a different schedule.');
                setCurrentStep(1); // Send user back to schedule selection
                setRegistrationData(prev => ({ ...prev, schedule: null })); // Clear the full schedule
                await fetchSchedules(); // Refresh schedules to show updated is_full status
            } else {
                setError(err.response?.data?.detail || 'Failed to submit registration. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Check if registration is allowed
    if (league.state !== 'Registration') {
        return (
            <Container className={styles.container}>
                <Card className="mt-4">
                    <CardBody className="text-center py-5">
                        <div className="mb-4">
                            <i className="fas fa-exclamation-circle text-warning" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mt-4 mb-3">Registration Not Available</h2>
                        <p className="lead text-muted">
                            Registration for <strong>{league.title}</strong> is currently not open.
                        </p>
                        <p className="text-muted">
                            Please check back later or contact us for more information.
                        </p>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    if (success) {
        return (
            <Container className={styles.container}>
                <Card className="mt-4">
                    <CardBody className="text-center py-5">
                        <div className={styles.successIcon}>
                            <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mt-4 mb-3">Registration Complete!</h2>
                        <p className="lead">
                            Team "<strong>{registrationData.teamName}</strong>" has been successfully registered for {league.title}.
                        </p>
                        
                        {(league.payment_link || league.payment_link_split || league.prize_payment_link || league.prize_payment_link_split) && (
                            <section className={styles.paymentSection}>
                                <div className={styles.paymentHeading}>
                                    <span className={styles.paymentHeadingIcon} aria-hidden="true">
                                        💳
                                    </span>
                                    <div>
                                        <h5 className="mb-1">Complete Your Payment</h5>
                                        <p className="text-muted mb-0">Choose the payment option that applies to your team.</p>
                                    </div>
                                </div>
                                <div className={styles.paymentGrid}>
                                    {(league.payment_link || league.payment_link_split) && (
                                        <div className={styles.paymentSection_}>
                                            <h6 className={styles.paymentSectionTitle}>Registration</h6>
                                            <div className={styles.paymentCardContainer}>
                                                {league.payment_link && (
                                                    <a
                                                        href={league.payment_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.paymentCard}
                                                    >
                                                        <div className={styles.paymentCardIcon}>
                                                            <FaUsers />
                                                        </div>
                                                        <div className={styles.paymentCardContent}>
                                                            <div className={styles.paymentCardTitle}>Team Registration</div>
                                                            <div className={styles.paymentCardDesc}>{league.payment_link_text}</div>
                                                        </div>
                                                        <div className={styles.paymentCardAction}>Pay Now →</div>
                                                    </a>
                                                )}
                                                {league.payment_link_split && (
                                                    <a
                                                        href={league.payment_link_split}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.paymentCard}
                                                    >
                                                        <div className={styles.paymentCardIcon}>
                                                            <FaUser />
                                                        </div>
                                                        <div className={styles.paymentCardContent}>
                                                            <div className={styles.paymentCardTitle}>Player Registration</div>
                                                            <div className={styles.paymentCardDesc}>{league.payment_link_split_text}</div>
                                                        </div>
                                                        <div className={styles.paymentCardAction}>Pay Now →</div>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {(league.prize_payment_link || league.prize_payment_link_split) && (
                                        <div className={styles.paymentSection_}>
                                            <h6 className={styles.paymentSectionTitle}>Prize Pool</h6>
                                            <div className={styles.paymentCardContainer}>
                                                {league.prize_payment_link && (
                                                    <a
                                                        href={league.prize_payment_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.paymentCard}
                                                    >
                                                        <div className={styles.paymentCardIcon}>
                                                            <FaTrophy />
                                                        </div>
                                                        <div className={styles.paymentCardContent}>
                                                            <div className={styles.paymentCardTitle}>Team Prize Pool</div>
                                                            <div className={styles.paymentCardDesc}>{league.prize_payment_link_text}</div>
                                                        </div>
                                                        <div className={styles.paymentCardAction}>Pay Now →</div>
                                                    </a>
                                                )}
                                                {league.prize_payment_link_split && (
                                                    <a
                                                        href={league.prize_payment_link_split}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.paymentCard}
                                                    >
                                                        <div className={styles.paymentCardIcon}>
                                                            <FaMedal />
                                                        </div>
                                                        <div className={styles.paymentCardContent}>
                                                            <div className={styles.paymentCardTitle}>Player Prize Pool</div>
                                                            <div className={styles.paymentCardDesc}>{league.prize_payment_link_split_text}</div>
                                                        </div>
                                                        <div className={styles.paymentCardAction}>Pay Now →</div>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <Container className={styles.container} style={{ paddingBottom: '40vh' }}>
            <Card className="mt-4">
                <CardBody>
                    <div className="text-center mb-2">
                        <h2>{league.title}</h2>
                    </div>
                    {currentStep === 1 && league.description && (
                        <div className="text-muted mb-4">
                            <ReactMarkdown>{league.description}</ReactMarkdown>
                        </div>
                    )}
                    

                    {/* Registration Steps Indicator */}
                    <div className={styles.stepsContainer}>
                        <div className="d-flex justify-content-center align-items-center mb-4">
                            <div className={`${styles.stepItem} ${currentStep >= 1 ? styles.stepActive : ''}`}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Team & Schedule</div>
                            </div>
                            <div className={styles.stepConnector}></div>
                            <div className={`${styles.stepItem} ${currentStep >= 2 ? styles.stepActive : ''}`}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Confirm</div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <Alert color="danger" className="mt-3" fade={false}>
                            {error}
                        </Alert>
                    )}

                    {currentStep === 1 && (
                        <div className="mt-4">
                            <h4 className="mb-3">Team & Player Information</h4>
                            <TeamPlayerForm
                                registrationData={registrationData}
                                updateRegistrationData={updateRegistrationData}
                                checkingTeamName={checkingTeamName}
                                teamNameAvailable={teamNameAvailable}
                                league={league}
                            />
                            
                            <hr className="my-4" />
                            
                            <h4 className="mb-3">Select a Schedule</h4>
                            <p className="text-muted mb-3">
                                Your team will have rotating tee times within the selected schedule. You can reschedule specific games as needed after your initial times are set.
                            </p>
                            <LeagueScheduleSelector
                                leagueGuid={league.guid}
                                schedules={schedules}
                                selectedSchedule={registrationData.schedule}
                                onSelectSchedule={(schedule) => updateRegistrationData({
                                    schedule,
                                    otherScheduleSelected: false,
                                    scoringSchedule: null,
                                    preferredDay: '',
                                    preferredTimeslot: null
                                })}
                                isOtherScheduleSelected={registrationData.otherScheduleSelected}
                                onSelectOtherSchedule={() => updateRegistrationData({
                                    schedule: null,
                                    otherScheduleSelected: true
                                })}
                                selectedScoringSchedule={registrationData.scoringSchedule}
                                onSelectScoringSchedule={(scoringSchedule) => updateRegistrationData({ scoringSchedule })}
                                selectedTimeslot={registrationData.preferredTimeslot}
                                onSelectAvailability={({ day, timeslot }) => updateRegistrationData({
                                    preferredDay: day,
                                    preferredTimeslot: timeslot
                                })}
                            />
                            
                            <div className="d-flex justify-content-end mt-4">
                                <Button 
                                    color="primary" 
                                    onClick={handleNext}
                                >
                                    Next <i className="fas fa-arrow-right ms-2"></i>
                                </Button>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="mt-4">
                            <h4 className="mb-3">Confirm Registration</h4>
                            <LeagueRegistrationConfirm
                                registrationData={registrationData}
                            />
                            <div className="d-flex justify-content-between mt-4">
                                <Button 
                                    color="secondary" 
                                    outline
                                    onClick={handleBack}
                                    disabled={loading}
                                >
                                    <i className="fas fa-arrow-left me-2"></i> Back
                                </Button>
                                <Button 
                                    color="success" 
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    size="lg"
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-check me-2"></i> Complete Registration
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
};

export default LeagueRegistrationForm;
